import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import ReportExporter from '../components/ReportExporter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Phone, 
  Mail, 
  Calendar, 
  User, 
  FileText,
  CheckCircle2,
  Clock,
  XCircle,
  Eye,
  Filter,
  Edit
} from 'lucide-react';
import { Input } from '../components/ui/input';
import {
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: Clock },
  contacted: { label: 'Contacted', color: 'bg-amber-100 text-amber-700', icon: Phone },
  converted: { label: 'Converted', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  rejected: { label: 'Not Interested', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const ENQUIRY_STATUSES = ['new', 'contacted', 'converted', 'rejected'];

const LOAN_TYPES = [
    "Housing Loan", "Personal Loan", "Mortgage Loan", "Business Loan",
    "Car Loan", "Loan Take Over", "LAP", "Poultry Loan",
    "Machinery Loan", "Education Loan", "CC Plus OD", "Commercial Vehicle"
];

const EnquiriesPage = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatuses, setSelectedStatuses] = useState([...ENQUIRY_STATUSES]);
  const [selectedTypes, setSelectedTypes] = useState([...LOAN_TYPES]);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [periodFilter, setPeriodFilter] = useState('all');
  const _today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(_today.getMonth() + 1);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(_today.getMonth() / 3) + 1);
  const [selectedPeriodYear, setSelectedPeriodYear] = useState(_today.getFullYear());
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const periodYears = [_today.getFullYear() - 2, _today.getFullYear() - 1, _today.getFullYear(), _today.getFullYear() + 1];
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEnquiry, setEditingEnquiry] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    email: '',
    loan_type: '',
    message: '',
    remarks: ''
  });

  // Toggle functions
  const toggleStatus = (status) => {
    setSelectedStatuses(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
  };
  const toggleType = (type) => {
    setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  // Calculate date range based on period filter
  // NOTE: We build YYYY-MM-DD strings manually to avoid the timezone bug where
  // new Date(y, m, 1).toISOString() converts to UTC, shifting IST dates by ~5.5h
  // and returning the previous day (e.g., 1 May IST -> 30 Apr UTC).
  const ymd = (y, m, d) => `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  const getDateRange = (period) => {
    const today = new Date();
    let start, end;
    
    switch(period) {
      case 'today':
        start = end = ymd(today.getFullYear(), today.getMonth() + 1, today.getDate());
        break;
      case 'week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = ymd(weekStart.getFullYear(), weekStart.getMonth() + 1, weekStart.getDate());
        end = ymd(today.getFullYear(), today.getMonth() + 1, today.getDate());
        break;
      }
      case 'month': {
        const m = selectedMonth;
        const y = selectedPeriodYear;
        const lastDay = new Date(y, m, 0).getDate();
        start = ymd(y, m, 1);
        end = ymd(y, m, lastDay);
        break;
      }
      case 'quarter': {
        const qStartMonth = (selectedQuarter - 1) * 3 + 1;
        const qEndMonth = qStartMonth + 2;
        const y = selectedPeriodYear;
        const lastDay = new Date(y, qEndMonth, 0).getDate();
        start = ymd(y, qStartMonth, 1);
        end = ymd(y, qEndMonth, lastDay);
        break;
      }
      case 'year':
        start = ymd(selectedPeriodYear, 1, 1);
        end = ymd(selectedPeriodYear, 12, 31);
        break;
      default:
        return null;
    }
    return { start, end };
  };

  // Filter enquiries by period
  const getFilteredByPeriod = (items) => {
    if (periodFilter === 'all') return items;
    
    const range = getDateRange(periodFilter);
    if (!range) return items;
    
    return items.filter(e => {
      const enquiryDate = e.created_at?.split('T')[0];
      if (!enquiryDate) return true;
      return enquiryDate >= range.start && enquiryDate <= range.end;
    });
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      const response = await axios.get(`${API}/inquiries`);
      // Sort by date descending (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setEnquiries(sorted);
    } catch (error) {
      toast.error('Error fetching enquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (enquiryId, newStatus) => {
    try {
      const response = await axios.put(`${API}/inquiries/${enquiryId}`, { status: newStatus });
      
      if (response.data.converted) {
        toast.success(
          `✅ Enquiry converted! Customer & Loan File created automatically.`,
          { duration: 5000 }
        );
      } else {
        toast.success('Status updated successfully');
      }
      
      fetchEnquiries();
    } catch (error) {
      toast.error('Error updating status');
    }
  };

  const handleViewDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setIsDialogOpen(true);
  };

  const handleEditClick = (enquiry) => {
    setEditingEnquiry(enquiry);
    setEditFormData({
      name: enquiry.name || '',
      phone: enquiry.phone || '',
      email: enquiry.email || '',
      loan_type: enquiry.loan_type || '',
      message: enquiry.message || '',
      remarks: enquiry.remarks || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    try {
      await axios.put(`${API}/inquiries/${editingEnquiry.id}`, editFormData);
      toast.success('Enquiry updated successfully');
      setIsEditDialogOpen(false);
      setEditingEnquiry(null);
      fetchEnquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating enquiry');
    }
  };

  const filteredEnquiries = getFilteredByPeriod(enquiries.filter(enquiry => 
    selectedStatuses.includes(enquiry.status) && selectedTypes.includes(enquiry.loan_type)
  ));

  const stats = {
    total: enquiries.length,
    new: enquiries.filter(i => i.status === 'new').length,
    contacted: enquiries.filter(i => i.status === 'contacted').length,
    converted: enquiries.filter(i => i.status === 'converted').length
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="enquiries-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Loan Enquiries</h1>
          <p className="text-slate-500 mt-1">Manage customer enquiries from website</p>
        </div>
        <ReportExporter type="enquiries" />
      </div>

      {/* Period Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b pb-2">
        {[
          { key: 'today', label: 'Today' },
          { key: 'week', label: 'This Week' },
          { key: 'month', label: 'This Month' },
          { key: 'quarter', label: 'This Quarter' },
          { key: 'year', label: 'This Year' },
          { key: 'all', label: 'All Time' }
        ].map(tab => (
          <Button
            key={tab.key}
            variant={periodFilter === tab.key ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriodFilter(tab.key)}
            className={periodFilter === tab.key ? 'bg-brand-navy text-white' : ''}
            data-testid={`enquiries-period-${tab.key}`}
          >
            {tab.label}
          </Button>
        ))}
        {periodFilter === 'month' && (
          <>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{monthNames.map((n, i) => <SelectItem key={i+1} value={(i+1).toString()}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        {periodFilter === 'quarter' && (
          <>
            <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
              <SelectTrigger className="w-[170px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
                <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
                <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
                <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        {periodFilter === 'year' && (
          <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-brand-navy">{stats.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats.new}</p>
                <p className="text-xs text-slate-500">New</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Phone className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats.contacted}</p>
                <p className="text-xs text-slate-500">Contacted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{stats.converted}</p>
                <p className="text-xs text-slate-500">Converted</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {/* Status Filter Panel */}
          {showStatusFilter && (
            <div className="border-b p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-slate-700">Select Status:</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setSelectedStatuses([...ENQUIRY_STATUSES])}>All</Button>
                  <Button size="sm" variant="outline" onClick={() => setSelectedStatuses([])}>Clear</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {ENQUIRY_STATUSES.map(status => (
                  <label key={status} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs ${selectedStatuses.includes(status) ? statusConfig[status]?.color : 'bg-white opacity-50'}`}>
                    <input type="checkbox" checked={selectedStatuses.includes(status)} onChange={() => toggleStatus(status)} className="w-3 h-3" />
                    {statusConfig[status]?.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          {/* Type Filter Panel */}
          {showTypeFilter && (
            <div className="border-b p-4 bg-blue-50">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold text-slate-700">Select Loan Types:</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setSelectedTypes([...LOAN_TYPES])}>All</Button>
                  <Button size="sm" variant="outline" onClick={() => setSelectedTypes([])}>Clear</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {LOAN_TYPES.map(type => (
                  <label key={type} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs ${selectedTypes.includes(type) ? 'bg-blue-100 border-blue-300' : 'bg-white opacity-50'}`}>
                    <input type="checkbox" checked={selectedTypes.includes(type)} onChange={() => toggleType(type)} className="w-3 h-3" />
                    {type}
                  </label>
                ))}
              </div>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Customer</TableHead>
                <TableHead className="font-semibold text-slate-600">Contact</TableHead>
                <TableHead className="font-semibold text-slate-600 cursor-pointer hover:bg-slate-100" onClick={() => setShowTypeFilter(!showTypeFilter)}>
                  <div className="flex items-center gap-1">
                    Loan Type
                    <Filter className={`h-3 w-3 ${selectedTypes.length < LOAN_TYPES.length ? 'text-brand-navy' : 'text-slate-400'}`} />
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-slate-600 cursor-pointer hover:bg-slate-100" onClick={() => setShowStatusFilter(!showStatusFilter)}>
                  <div className="flex items-center gap-1">
                    Status
                    <Filter className={`h-3 w-3 ${selectedStatuses.length < ENQUIRY_STATUSES.length ? 'text-brand-navy' : 'text-slate-400'}`} />
                  </div>
                </TableHead>
                <TableHead className="font-semibold text-slate-600">Remarks</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEnquiries.map((enquiry) => {
                const StatusIcon = statusConfig[enquiry.status]?.icon || Clock;
                return (
                  <TableRow key={enquiry.id} className="hover:bg-slate-50/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-brand-navy rounded-full flex items-center justify-center">
                          <span className="text-sm font-bold text-brand-gold">
                            {enquiry.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <span className="font-medium">{enquiry.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-3 w-3 text-slate-400" />
                          <a href={`tel:${enquiry.phone}`} className="text-brand-navy hover:underline">
                            {enquiry.phone}
                          </a>
                        </div>
                        {enquiry.email && (
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Mail className="h-3 w-3 text-slate-400" />
                            {enquiry.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-brand-gold/10 text-brand-navy border-brand-gold/30">
                        {enquiry.loan_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={enquiry.status}
                        onValueChange={(value) => handleStatusChange(enquiry.id, value)}
                      >
                        <SelectTrigger 
                          className={`w-[140px] h-8 text-xs ${statusConfig[enquiry.status]?.color || 'bg-slate-100'}`}
                        >
                          <div className="flex items-center gap-1">
                            <StatusIcon className="h-3 w-3" />
                            <SelectValue />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                          <SelectItem value="rejected">Not Interested</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate text-slate-600 text-sm">
                      {enquiry.remarks || '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditClick(enquiry)}
                          data-testid={`edit-enquiry-${enquiry.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleViewDetails(enquiry)}
                          data-testid={`view-enquiry-${enquiry.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredEnquiries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No enquiries found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">Enquiry Details</DialogTitle>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-brand-navy rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold text-brand-gold">
                    {selectedEnquiry.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-bold text-brand-navy text-lg">{selectedEnquiry.name}</h3>
                  <Badge className={statusConfig[selectedEnquiry.status]?.color}>
                    {statusConfig[selectedEnquiry.status]?.label}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Phone</p>
                  <a 
                    href={`tel:${selectedEnquiry.phone}`}
                    className="flex items-center gap-2 text-brand-navy font-medium hover:underline"
                  >
                    <Phone className="h-4 w-4" />
                    {selectedEnquiry.phone}
                  </a>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Email</p>
                  <p className="flex items-center gap-2 text-slate-700">
                    <Mail className="h-4 w-4 text-slate-400" />
                    {selectedEnquiry.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Loan Type</p>
                  <p className="flex items-center gap-2 font-medium text-brand-navy">
                    <FileText className="h-4 w-4 text-brand-gold" />
                    {selectedEnquiry.loan_type}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Date</p>
                  <p className="flex items-center gap-2 text-slate-700">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    {formatDate(selectedEnquiry.created_at)}
                  </p>
                </div>
              </div>

              {selectedEnquiry.message && (
                <div className="p-4 bg-amber-50 rounded-lg border-l-4 border-amber-500">
                  <p className="text-xs text-slate-500 mb-2">Message</p>
                  <p className="text-slate-700">{selectedEnquiry.message}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <a href={`tel:${selectedEnquiry.phone}`} className="flex-1">
                  <Button className="w-full bg-brand-navy text-white">
                    <Phone className="h-4 w-4 mr-2" />
                    Call Now
                  </Button>
                </a>
                {selectedEnquiry.email && (
                  <a href={`mailto:${selectedEnquiry.email}`}>
                    <Button variant="outline">
                      <Mail className="h-4 w-4 mr-2" />
                      Email
                    </Button>
                  </a>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">Edit Enquiry</DialogTitle>
            <DialogDescription>Update enquiry details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={editFormData.name}
                onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="edit-enquiry-name"
                placeholder="Customer name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editFormData.phone}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, phone: e.target.value }))}
                  data-testid="edit-enquiry-phone"
                  placeholder="Phone number"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="edit-enquiry-email"
                  placeholder="Email address"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Loan Type</Label>
              <Select
                value={editFormData.loan_type}
                onValueChange={(v) => setEditFormData(prev => ({ ...prev, loan_type: v }))}
              >
                <SelectTrigger data-testid="edit-enquiry-loan-type">
                  <SelectValue placeholder="Select loan type" />
                </SelectTrigger>
                <SelectContent>
                  {LOAN_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Input
                value={editFormData.message}
                onChange={(e) => setEditFormData(prev => ({ ...prev, message: e.target.value }))}
                data-testid="edit-enquiry-message"
                placeholder="Customer message"
              />
            </div>
            <div className="space-y-2">
              <Label>Remarks (Staff Notes)</Label>
              <Input
                value={editFormData.remarks}
                onChange={(e) => setEditFormData(prev => ({ ...prev, remarks: e.target.value }))}
                data-testid="edit-enquiry-remarks"
                placeholder="Add staff notes or remarks"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleEditSubmit}
              data-testid="submit-edit-enquiry-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
            >
              Update Enquiry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnquiriesPage;
